import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiAlertCircle, FiArrowLeft, FiCheckCircle, FiInfo } from "react-icons/fi";
import { Header } from "../../components";
import { useStateContext } from "../../contexts/ContextProvider";
import useKYCStatusRedirect from "../../hooks/useKYCStatusRedirect";
import { userRequest } from "../../utils/requestMethods";
import ButtonLoading from "../../components/ButtonLoading";
import StaffBankDetailsModal from "../../components/Finance/StaffBankDetailsModal";

const PayrollSetup = () => {
  useKYCStatusRedirect("/finance/payroll/setup");
  const { school } = useStateContext();
  const schoolId = school?._id;

  const [staff, setStaff] = useState([]);
  const [salaries, setSalaries] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [bankModalStaff, setBankModalStaff] = useState(null);
  const [generatingWalletId, setGeneratingWalletId] = useState(null);

  const handleGenerateWallet = async (member) => {
    setGeneratingWalletId(member._id);
    setError("");
    try {
      const res = await userRequest.post("/banking/virtual-accounts/staff/bulk", {
        schoolId,
        staff: [{ staffId: member._id, staffName: `${member.firstname} ${member.lastname}` }],
        createWalletIfNotExists: true,
      });
      const resultItem = res.data?.results?.[0] || res.data;
      const generatedAcc = resultItem?.accountNumber || resultItem?.account_number || res.data?.accountNumber || "GENERATED";
      setStaff((prev) =>
        prev.map((s) =>
          s._id === member._id ? { ...s, account_number: generatedAcc, account_id: generatedAcc } : s
        )
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to generate staff virtual wallet.");
    } finally {
      setGeneratingWalletId(null);
    }
  };

  const fetchStaff = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [teachersRes, adminsRes] = await Promise.all([
        userRequest.get(`/teachers?school_id=${schoolId}&limit=100`),
        userRequest.get(`/schools/${schoolId}/admins?limit=100`),
      ]);
      const teachers = (teachersRes.data.teachers || []).map((t) => ({
        ...t,
        roleLabel: "Teacher",
      }));
      const admins = (adminsRes.data.users || []).map((a) => ({
        ...a,
        roleLabel: a.role === 2 ? "Burser" : "Admin",
      }));
      const allStaff = [...teachers, ...admins];
      setStaff(allStaff);

      // Pre-fill existing salaries from staff metadata
      const initialSalaries = {};
      allStaff.forEach((s) => {
        if (s.metadata?.monthly_salary) {
          initialSalaries[s._id] = s.metadata.monthly_salary;
        }
      });
      setSalaries(initialSalaries);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const submitHandler = async (e) => {
    e.preventDefault();
    const staffPayload = Object.entries(salaries)
      .filter(([, value]) => value && Number(value) > 0)
      .map(([staff_id, monthly_salary]) => ({ staff_id, monthly_salary: Number(monthly_salary) }));

    if (staffPayload.length === 0) {
      setError("Enter at least one monthly salary before saving.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      await userRequest.put(`/schools/${schoolId}/finance/staff-salaries`, { staff: staffPayload });
      setSuccess(true);
      fetchStaff();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save salaries. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex items-start justify-between mb-2">
        <Header
          category="Finance"
          title="Payroll — Set Salaries"
          description="Configure monthly salaries for eligible staff. Staff members also need linked bank account details to receive salary disbursements."
        />
        <Link
          to="/finance/payroll/batches"
          className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors shrink-0"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back to Batches
        </Link>
      </div>

      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-4 py-3 text-sm my-6">
        <FiInfo className="mt-0.5 shrink-0" />
        <span>
          Set a monthly salary for each staff member you want to pay through payroll. For
          disbursements, staff must also have their bank account details configured in their user
          profile.
        </span>
      </div>

      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiCheckCircle className="mt-0.5 shrink-0" />
          <span>
            <b>Saved!</b> Salaries have been updated.
          </span>
        </div>
      )}

      <form onSubmit={submitHandler}>
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                  Staff Name
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                  Role
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                  Bank Account (NIP)
                </th>
                {/* Acceede Wallet column — commented out for now */}
                {/* <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                  Acceede Wallet
                </th> */}
                <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                  Monthly Salary (₦)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-sm text-gray-400">
                    Loading staff...
                  </td>
                </tr>
              ) : staff.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-sm text-gray-400">
                    No eligible staff found.
                  </td>
                </tr>
              ) : (
                staff.map((member) => {
                  const cp = member.counter_party;
                  const hasBank = Boolean(cp?.account_number?.trim() && cp?.bank_code?.trim());
                  // Acceede Wallet column commented out for now.
                  // const hasWallet = Boolean(member.account_number?.trim() || member.account_id?.trim());

                  return (
                    <tr key={member._id}>
                      <td className="px-5 py-3 text-sm font-medium text-gray-800">
                        {member.firstname} {member.lastname}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-500">{member.roleLabel}</td>
                      <td className="px-5 py-3 text-sm">
                        {hasBank ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {cp.bank_name || "Bank"} • {cp.account_number}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setBankModalStaff(member)}
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
                          >
                            <FiAlertCircle className="w-3 h-3" /> Missing Bank Account
                          </button>
                        )}
                      </td>
                      {/* Acceede Wallet column — commented out for now */}
                      {/* <td className="px-5 py-3 text-sm">
                        {hasWallet ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Acceede • {member.account_number}
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={generatingWalletId === member._id}
                            onClick={() => handleGenerateWallet(member)}
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                          >
                            {generatingWalletId === member._id ? "Generating..." : "+ Generate Wallet"}
                          </button>
                        )}
                      </td> */}
                      <td className="px-5 py-3">
                        <input
                          type="number"
                          value={salaries[member._id] || ""}
                          onChange={(e) =>
                            setSalaries((prev) => ({ ...prev, [member._id]: e.target.value }))
                          }
                          placeholder="e.g. 75000"
                          className="w-40 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && staff.length > 0 && (
          <div className="mt-6">
            {saving ? (
              <ButtonLoading styles="text-white bg-indigo-500 font-medium rounded-full text-sm px-6 py-2.5 text-center" />
            ) : (
              <button
                type="submit"
                className="py-2.5 px-6 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-full transition-colors"
              >
                Save Salaries
              </button>
            )}
          </div>
        )}
      </form>
      <StaffBankDetailsModal
        isOpen={Boolean(bankModalStaff)}
        onClose={() => setBankModalStaff(null)}
        schoolId={schoolId}
        staffMember={bankModalStaff}
        onSaved={(staffId, updatedCp) => {
          setStaff(prev => prev.map(m => m._id === staffId ? { ...m, counter_party: updatedCp } : m));
          setBankModalStaff(null);
        }}
      />
    </div>
  );
};

export default PayrollSetup;
